<script setup lang="ts">
import type { Diagnostic } from "@/types";
import type { Finding } from "@/types/findings.types";
import { computed, nextTick, ref } from "vue";
import { FileType } from "@/types/file.types";
import ReportState from "@/stores/report/ReportState";
import {
  useGetReportSectionsTree,
  useDeleteManyDiagnosticImages,
  useCreateFinding,
} from "@/stores/queries";
import {
  useUpdateReportImage,
  useSortReportImageToSection,
  useExcludeReportImage,
  useCreateReportImage,
  useAddManyReportImagesToFinding,
} from "@/stores/queries/report-images.query";
import { useMergedReportImages } from "@/composable/useMergedReportImages";

// Custom directive for autofocus
const vFocus = {
  mounted: (el: HTMLElement) => {
    nextTick(() => {
      const input = el.querySelector("input, textarea") as HTMLElement;
      input?.focus();
    });
  },
};
import type { MergedReportImage } from "@/types/report-images.types";
import type { SectionItem } from "@/types/sections.types";
import VisualiseImage from "../findings/VisualiseImage.vue";
import DRFilesUploaderDialog from "@/components/UI/DRFilesUploaderDialog.vue";
import PhotoActionsDropdown from "./PhotoActionsDropdown.vue";
import UpdateFindingDialog from "../findings/finding-dialog/UpdateFindingDialog.vue";
import SectionActionsButton from "../../sections/SectionActionsButton.vue";
import PhotoSelectionActionBar from "./PhotoSelectionActionBar.vue";
import MoveSectionModal from "./MoveSectionModal.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";

const props = defineProps<{
  diagnostic: Diagnostic;
  reportId: number;
}>();

const reportId = computed(() => props.reportId);

const { mergedImages, refetch: refetchImages } = useMergedReportImages(
  computed(() => props.diagnostic.id),
  reportId,
);
const { data: sectionsTree } = useGetReportSectionsTree(reportId);

const { mutate: updateReportImage } = useUpdateReportImage(reportId);
const { mutateAsync: sortToSection } = useSortReportImageToSection(reportId);
const { mutateAsync: excludeReportImage } = useExcludeReportImage(reportId);
const { mutateAsync: createReportImage } = useCreateReportImage(reportId);
const { mutate: deleteManyImages, isPending: isDeletingMany } =
  useDeleteManyDiagnosticImages(computed(() => props.diagnostic.id));
const { mutateAsync: createFinding } = useCreateFinding();
const { mutateAsync: addManyImagesToFinding, isPending: isCreatingFinding } =
  useAddManyReportImagesToFinding(reportId);

const showUploadPhotos = ref(false);

// Selection state - stores diagnosticImageIds
const selectedImages = ref<Set<number>>(new Set());

const toggleSelection = (diagnosticImageId: number) => {
  if (selectedImages.value.has(diagnosticImageId)) {
    selectedImages.value.delete(diagnosticImageId);
  } else {
    selectedImages.value.add(diagnosticImageId);
  }
};

const isSelected = (diagnosticImageId: number) =>
  selectedImages.value.has(diagnosticImageId);

const clearSelection = () => {
  selectedImages.value.clear();
};

// Helper to ensure a reportImageId exists (create if virtual)
const ensureReportImageId = async (
  image: MergedReportImage,
): Promise<number | null> => {
  if (image.reportImageId) return image.reportImageId;
  if (image.isVirtual) {
    const newReportImage = await createReportImage({
      diagnosticImageId: image.diagnosticImageId,
      reportId: props.reportId,
    });
    return newReportImage.id;
  }
  return null;
};

// Bulk actions
const showBulkMoveDialog = ref(false);
const showDeleteConfirm = ref(false);

const bulkMoveToSection = async (sectionId: number | null) => {
  for (const diagnosticImageId of selectedImages.value) {
    const image = sortedImages.value.find(
      (img) => img.diagnosticImageId === diagnosticImageId,
    );
    if (image) {
      const reportImageId = await ensureReportImageId(image);
      if (reportImageId) {
        await sortToSection({ imageId: reportImageId, sectionId });
      }
    }
  }
  clearSelection();
};

const bulkExclude = async () => {
  for (const diagnosticImageId of selectedImages.value) {
    const image = sortedImages.value.find(
      (img) => img.diagnosticImageId === diagnosticImageId,
    );
    if (image) {
      const reportImageId = await ensureReportImageId(image);
      if (reportImageId) {
        await excludeReportImage(reportImageId);
      }
    }
  }
  clearSelection();
};

const confirmBulkDelete = () => {
  showDeleteConfirm.value = true;
};

const bulkDelete = () => {
  const ids = [...selectedImages.value];
  deleteManyImages(ids, {
    onSuccess: () => {
      clearSelection();
      showDeleteConfirm.value = false;
    },
  });
};

// Create finding from selected images
const showSectionSelectDialog = ref(false);
const pendingCreateFindingImages = ref<MergedReportImage[]>([]);

const getSelectedImagesData = () => {
  const ids = [...selectedImages.value];
  return sortedImages.value.filter((img) =>
    ids.includes(img.diagnosticImageId),
  );
};

const handleCreateFinding = async () => {
  const selectedImagesData = getSelectedImagesData();

  // Get unique section IDs from selected images
  const sectionIds = new Set(
    selectedImagesData
      .map((img) => img.sortedSectionId)
      .filter((id): id is number => id !== null && id !== undefined),
  );

  // If all images belong to the same section, create finding directly
  if (sectionIds.size === 1) {
    const sectionId = [...sectionIds][0];
    await createFindingWithImages(sectionId, selectedImagesData);
  } else {
    // Show section selection dialog
    pendingCreateFindingImages.value = selectedImagesData;
    showSectionSelectDialog.value = true;
  }
};

const createFindingWithImages = async (
  sectionId: number,
  images: MergedReportImage[],
) => {
  // Copy custom location from the first image that has one
  const imageWithLocation = images.find((img) => img.location?.trim());
  const location = imageWithLocation?.location?.trim() || undefined;

  const finding = await createFinding({
    reportId: props.reportId,
    sectionId,
    location,
  });
  if (finding) {
    // Ensure all images have reportImageIds
    const reportImageIds: number[] = [];
    for (const img of images) {
      const reportImageId = await ensureReportImageId(img);
      if (reportImageId) {
        reportImageIds.push(reportImageId);
      }
    }

    await addManyImagesToFinding({
      findingId: finding.id,
      imageIds: reportImageIds,
    });

    clearSelection();

    // Open the finding dialog
    selectedFinding.value = finding;
    selectedFindingSectionId.value = sectionId;
    showFindingDialog.value = true;
  }
};

const onSectionSelectedForFinding = async (sectionId: number | null) => {
  if (sectionId !== null && pendingCreateFindingImages.value.length > 0) {
    await createFindingWithImages(sectionId, pendingCreateFindingImages.value);
  }
  pendingCreateFindingImages.value = [];
};

// Inline editing state (tracks both row and field)
const editingId = ref<number | null>(null);
const editingField = ref<string | null>(null);

const startEditing = (id: number, field: string) => {
  editingId.value = id;
  editingField.value = field;
};

const stopEditing = () => {
  editingId.value = null;
  editingField.value = null;
};

const isEditing = (id: number, field: string) =>
  editingId.value === id && editingField.value === field;

// Finding dialog state
const selectedFinding = ref<Finding | null>(null);
const selectedFindingSectionId = ref<number | null>(null);
const showFindingDialog = ref(false);

const openFindingDialog = (finding: Finding) => {
  selectedFinding.value = finding;
  selectedFindingSectionId.value = finding.sectionId ?? null;
  showFindingDialog.value = true;
};

// Collapsed state for groups (persisted in ReportState)
const toggleGroup = (groupId: string) => {
  ReportState.togglePhotosGroup(groupId);
};

const isGroupCollapsed = (groupId: string) =>
  ReportState.isPhotosGroupCollapsed(groupId);

// Sort images by diagnosticImageId to maintain stable order across re-fetches
const sortedImages = computed(() => {
  if (!mergedImages.value) return [];
  return [...mergedImages.value].sort(
    (a, b) => a.diagnosticImageId - b.diagnosticImageId,
  );
});

type GroupItem = {
  id: string;
  title: string;
  path?: string;
  images: MergedReportImage[];
  parentIds: string[]; // Track parent section IDs for collapse logic
  section?: SectionItem; // The section object for actions
  hasChildren: boolean;
  recursiveImageCount: number; // Total images in section + all children
};

// Helper to calculate recursive image count for a section
const getRecursiveImageCount = (
  section: SectionItem,
  imagesBySectionId: Map<number, number>,
): number => {
  let count = imagesBySectionId.get(section.id) || 0;
  if (section.children?.length) {
    for (const child of section.children) {
      count += getRecursiveImageCount(child, imagesBySectionId);
    }
  }
  return count;
};

const flattenSectionsWithImages = computed(() => {
  if (!sortedImages.value.length || !sectionsTree.value) return [];

  // Pre-calculate image counts by section for efficiency
  const imagesBySectionId = new Map<number, number>();
  for (const img of sortedImages.value) {
    if (img.sortedSectionId) {
      imagesBySectionId.set(
        img.sortedSectionId,
        (imagesBySectionId.get(img.sortedSectionId) || 0) + 1,
      );
    }
  }

  const result: GroupItem[] = [];

  // First: unclassified images (no sortedSectionId)
  const unclassifiedImages = sortedImages.value.filter(
    (img) => !img.sortedSectionId,
  );
  if (unclassifiedImages.length > 0) {
    result.push({
      id: "unclassified",
      title: "report:photos.sorting.unclassified",
      images: unclassifiedImages,
      parentIds: [],
      hasChildren: false,
      recursiveImageCount: unclassifiedImages.length,
    });
  }

  // Then: flatten sections tree and add their images
  const flattenSections = (
    sections: SectionItem[],
    parentIds: string[] = [],
  ): void => {
    for (const section of sections) {
      const sectionImages = sortedImages.value.filter(
        (img) => img.sortedSectionId === section.id,
      );
      const currentId = `section-${section.id}`;
      const hasChildren = !!(section.children && section.children.length > 0);
      const recursiveImageCount = getRecursiveImageCount(
        section,
        imagesBySectionId,
      );

      result.push({
        id: currentId,
        title: section.title,
        path: section.path,
        images: sectionImages,
        parentIds,
        section,
        hasChildren,
        recursiveImageCount,
      });

      if (section.children?.length) {
        flattenSections(section.children, [...parentIds, currentId]);
      }
    }
  };

  flattenSections(sectionsTree.value);

  return result;
});

// Check if group is collapsed (either directly or via a collapsed parent)
const isGroupHidden = (group: GroupItem): boolean => {
  // Check if any parent is collapsed
  for (const parentId of group.parentIds) {
    if (ReportState.isPhotosGroupCollapsed(parentId)) {
      return true;
    }
  }
  return false;
};

const detailsImageWidth = computed(() => {
  return ReportState.getPhotoWidth("details");
});

type EditableField = "description" | "location" | "numbering";

const handleUpdate = async (
  image: MergedReportImage,
  field: EditableField,
  value: any,
) => {
  // Ensure reportImageId exists
  const reportImageId = await ensureReportImageId(image);
  if (reportImageId) {
    updateReportImage({
      id: reportImageId,
      payload: { [field]: value },
    });
  }
};
</script>

<template>
  <div class="photos-details h-100 d-flex flex-column">
    <!-- Selection action bar -->
    <PhotoSelectionActionBar
      v-if="selectedImages.size > 0"
      :selected-count="selectedImages.size"
      :is-deleting="isDeletingMany"
      :is-creating-finding="isCreatingFinding"
      show-create-finding
      @move="showBulkMoveDialog = true"
      @exclude="bulkExclude"
      @delete="confirmBulkDelete"
      @create-finding="handleCreateFinding"
      @clear="clearSelection"
    />

    <!-- Scrollable content -->
    <el-scrollbar class="flex-grow-1">
      <div class="p-4">
        <!-- Empty state -->
        <div
          v-if="!mergedImages?.length"
          class="empty-state d-flex flex-column align-items-center justify-content-center py-5 text-muted"
        >
          <el-icon size="64" class="mb-3"><i class="bi-images"></i></el-icon>
          <p>{{ $t("report:photos.empty") }}</p>
          <button
            class="btn btn-outline-primary mt-2"
            @click="showUploadPhotos = true"
          >
            <el-icon class="me-2"><i class="bi-plus-lg"></i></el-icon>
            {{ $t("report:photos.import") }}
          </button>
        </div>

        <!-- Groups -->
        <div v-else class="groups-container">
          <div
            v-for="group in flattenSectionsWithImages"
            v-show="!isGroupHidden(group)"
            :key="group.id"
            class="group-section mb-3"
            :style="{
              marginLeft:
                group.id === 'unclassified'
                  ? '0'
                  : `${group.parentIds.length * 16}px`,
            }"
          >
            <!-- Group header -->
            <div
              class="group-header d-flex align-items-center gap-4 py-3 px-3 cursor-pointer"
              @click="toggleGroup(group.id)"
            >
              <el-icon size="16" class="text-muted">
                <i
                  :class="
                    isGroupCollapsed(group.id)
                      ? 'bi-chevron-right'
                      : 'bi-chevron-down'
                  "
                ></i>
              </el-icon>
              <div class="flex-grow-1 d-flex align-items-center gap-2">
                <span class="fw-medium">
                  <div
                    v-if="group.id === 'unclassified'"
                    class="d-flex gap-2 align-items-center"
                  >
                    <el-icon size="16" class="text-muted">
                      <i class="bi-inbox"></i>
                    </el-icon>
                    <span> {{ $t(group.title) }} </span>
                  </div>
                  <template v-else>
                    {{ group.path }}. {{ group.title }}
                  </template>
                </span>
                <span
                  v-if="
                    isGroupCollapsed(group.id)
                      ? group.recursiveImageCount > 0
                      : !!group.images.length
                  "
                  class="badge bg-primary ms-2 py-1"
                >
                  {{
                    isGroupCollapsed(group.id)
                      ? group.recursiveImageCount
                      : group.images.length
                  }}
                  {{
                    $t("report:photos.stats.photo", {
                      count: isGroupCollapsed(group.id)
                        ? group.recursiveImageCount
                        : group.images.length,
                    })
                  }}
                </span>
              </div>

              <!-- Section actions button -->
              <SectionActionsButton
                v-if="group.section"
                class="ms-auto"
                :size="32"
                :section="group.section"
                :report-id="reportId"
                @click.stop
              />
            </div>

            <!-- Group content (table) -->
            <div
              v-if="!isGroupCollapsed(group.id) && group.images.length"
              class="group-content mt-1 rounded bordered"
            >
              <el-table
                :data="group.images"
                size="small"
                style="width: 100%"
                row-key="diagnosticImageId"
                :row-class-name="
                  ({ row }) =>
                    [
                      row.excluded ? 'excluded-row' : '',
                      isSelected(row.diagnosticImageId) ? 'selected-row' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')
                "
              >
                <!-- Checkbox column -->
                <el-table-column width="50" align="center">
                  <template #default="{ row }">
                    <div
                      class="checkbox-cell"
                      @click.stop="toggleSelection(row.diagnosticImageId)"
                    >
                      <el-checkbox
                        :model-value="isSelected(row.diagnosticImageId)"
                        size="large"
                        class="selection-checkbox"
                        @click.stop
                        @change="toggleSelection(row.diagnosticImageId)"
                      />
                    </div>
                  </template>
                </el-table-column>

                <el-table-column :width="detailsImageWidth + 40">
                  <template #default="{ row }">
                    <div class="shadow-1 m-2">
                      <VisualiseImage
                        v-if="row.file"
                        :id="row.file.id"
                        class="table-image"
                        :style="{
                          width: detailsImageWidth + 'px',
                          aspectRatio: '4 / 3',
                        }"
                        fill
                        previewMode="overlay"
                      />
                    </div>
                  </template>
                </el-table-column>

                <el-table-column
                  :label="$t('report:photos.column.photoNumber')"
                  width="110"
                >
                  <template #default="{ row }">
                    <span :class="{ 'text-muted': row.photoNumber == null }">
                      {{ row.photoNumber ?? "-" }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column
                  :label="$t('report:photos.column.numbering')"
                  width="130"
                >
                  <template #default="{ row }">
                    <div
                      class="clickable-cell"
                      @click="startEditing(row.diagnosticImageId, 'numbering')"
                    >
                      <el-input-number
                        v-if="isEditing(row.diagnosticImageId, 'numbering')"
                        v-focus
                        v-model="row.numbering"
                        :min="1"
                        :controls="false"
                        class="w-100"
                        @blur="
                          handleUpdate(row, 'numbering', row.numbering);
                          stopEditing();
                        "
                        @click.stop
                      />
                      <span
                        v-else
                        :class="{ 'text-muted': row.numbering == null }"
                      >
                        {{ row.numbering ?? "-" }}
                      </span>
                    </div>
                  </template>
                </el-table-column>

                <el-table-column
                  :label="$t('report:photos.column.description')"
                  min-width="200"
                >
                  <template #default="{ row }">
                    <div
                      class="clickable-cell"
                      @click="
                        startEditing(row.diagnosticImageId, 'description')
                      "
                    >
                      <el-input
                        v-if="isEditing(row.diagnosticImageId, 'description')"
                        v-focus
                        v-model="row.description"
                        type="textarea"
                        :autosize="{ minRows: 2, maxRows: 6 }"
                        @blur="
                          handleUpdate(row, 'description', row.description);
                          stopEditing();
                        "
                        @click.stop
                      />
                      <span v-else :class="{ 'text-muted': !row.description }">
                        {{ row.description || "-" }}
                      </span>
                    </div>
                  </template>
                </el-table-column>

                <el-table-column
                  :label="$t('report:photos.column.location')"
                  min-width="180"
                >
                  <template #default="{ row }">
                    <div
                      class="clickable-cell"
                      @click="startEditing(row.diagnosticImageId, 'location')"
                    >
                      <el-input
                        v-if="isEditing(row.diagnosticImageId, 'location')"
                        v-focus
                        v-model="row.location"
                        type="textarea"
                        :autosize="{ minRows: 2, maxRows: 6 }"
                        @blur="
                          handleUpdate(row, 'location', row.location);
                          stopEditing();
                        "
                        @click.stop
                      />
                      <span v-else :class="{ 'text-muted': !row.location }">
                        {{ row.location || "-" }}
                      </span>
                    </div>
                  </template>
                </el-table-column>

                <el-table-column
                  :label="$t('report:photos.column.findings')"
                  min-width="100"
                >
                  <template #default="{ row }">
                    <div
                      v-if="row.findings?.length"
                      class="d-flex flex-wrap gap-1"
                    >
                      <el-tooltip
                        v-for="finding in row.findings"
                        :key="finding.id"
                        placement="top"
                        :show-after="300"
                      >
                        <template #content>
                          <div class="finding-tooltip">
                            <div
                              v-if="finding.defaultNumbering"
                              class="fw-bold"
                            >
                              N° {{ finding.defaultNumbering }}
                            </div>
                            <div>
                              {{
                                finding.name || $t("report:diagnostics.noname")
                              }}
                            </div>
                            <div
                              v-if="finding.notation"
                              class="small text-muted"
                            >
                              Note: {{ finding.notation }}
                            </div>
                          </div>
                        </template>
                        <span
                          class="badge p-2 bg-dark cursor-pointer"
                          @click.stop="openFindingDialog(finding)"
                        >
                          {{
                            finding.defaultNumbering ||
                            finding.name?.substring(0, 8) ||
                            $t("report:findings.noName")
                          }}
                        </span>
                      </el-tooltip>
                    </div>
                    <span v-else class="text-muted">-</span>
                  </template>
                </el-table-column>

                <el-table-column width="80" align="center">
                  <template #default="{ row }">
                    <PhotoActionsDropdown
                      :image="row"
                      :diagnostic-id="diagnostic.id"
                      :report-id="reportId"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- Empty group message (hidden if section has children) -->
            <div
              v-else-if="
                !isGroupCollapsed(group.id) &&
                group.images.length === 0 &&
                !group.hasChildren
              "
              class="group-empty text-muted small py-3 ps-5 bg-white rounded bordered mt-2"
            >
              {{ $t("report:photos.groupEmpty") }}
            </div>
          </div>
        </div>
      </div>
    </el-scrollbar>
  </div>

  <!-- Upload dialog -->
  <DRFilesUploaderDialog
    v-model="showUploadPhotos"
    :title="$t('report:photos.import')"
    :file-type="FileType.DIAGNOSTIC_IMAGE"
    :file-params="{ diagnosticId: diagnostic.id }"
    :upload-description="
      ({ maxFiles, maxSizeMb }) =>
        $t('report:only.image.max.size', { maxFiles, maxSizeMb })
    "
    multiple
    images-only
    :extensions="['jpg', 'jpeg', 'png', 'heic', 'gif', 'bmp']"
    @uploaded="refetchImages()"
  />

  <!-- Finding dialog -->
  <UpdateFindingDialog
    v-if="selectedFinding"
    v-model="showFindingDialog"
    :finding="selectedFinding"
    :section-id="selectedFindingSectionId ?? 0"
    :diagnostic-id="diagnostic.id"
    :report-id="reportId"
    @update:model-value="(val) => !val && (selectedFinding = null)"
  />

  <!-- Move to section dialog (bulk) -->
  <MoveSectionModal
    v-model="showBulkMoveDialog"
    :report-id="reportId"
    @select="bulkMoveToSection"
  />

  <!-- Section selection dialog for creating finding -->
  <MoveSectionModal
    v-model="showSectionSelectDialog"
    :report-id="reportId"
    :title="$t('report:photos.actions.selectSectionForFinding')"
    hide-unclassified
    @select="onSectionSelectedForFinding"
  />

  <!-- Delete confirmation modal -->
  <ConfirmationModal
    v-model="showDeleteConfirm"
    :title="$t('report:photos.deleteConfirm.title')"
    :message="
      $t('report:photos.deleteConfirm.message', { count: selectedImages.size })
    "
    :confirm-label="$t('delete')"
    :loading="isDeletingMany"
    danger
    @confirm="bulkDelete"
  />
</template>

<style lang="scss" scoped>
.photos-details {
  min-width: 0;
  flex: 1;
  background-color: var(--bs-light);
}

.group-header {
  background-color: transparent;
  transition: background-color 0.15s;
  font-size: 0.9rem;

  &:hover {
    background-color: var(--bs-gray-200);
  }
}

.group-content {
  background-color: var(--bs-white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.cursor-pointer {
  cursor: pointer;
}

.table-image {
  overflow: hidden;
  border-radius: 4px;
}

.clickable-cell {
  cursor: pointer;
  min-height: 40px;
  display: flex;
  align-items: flex-start;
  padding: 4px;
  margin: -4px;
  border-radius: 4px;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--bs-gray-200);
  }

  span {
    white-space: pre-line;
    word-break: break-word;
  }
}

.empty-state {
  min-height: 400px;
}

:deep(.excluded-row) {
  opacity: 0.5;
}

:deep(.selected-row) {
  background-color: var(--el-color-primary-light-9) !important;
}

.checkbox-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 50px;
  cursor: pointer;
}

:deep(.selection-checkbox) {
  .el-checkbox__inner {
    width: 22px;
    height: 22px;
    border-width: 2px;
    border-color: var(--bs-gray-400);

    &::after {
      height: 10px;
      width: 5px;
      left: 6px;
      top: 2px;
      border-width: 2px;
    }
  }

  .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: var(--bs-primary);
    border-color: var(--bs-primary);
  }
}

:deep(.el-table) {
  --el-table-border-color: var(--bs-border-color);
  --el-table-row-hover-bg-color: transparent;

  .el-table__row {
    td {
      padding: 8px 12px;
      position: relative;
    }

    &:hover td {
      background-color: rgba(0, 0, 0, 0.04) !important;
    }
  }

  .el-table__header-wrapper th {
    padding: 4px 12px;
    background-color: var(--bs-gray-100);
  }
}

.finding-tooltip {
  max-width: 250px;
}
</style>
